// i18n bootstrap — web twin of the mobile app's lib/i18n/index.ts. The locale
// JSONs under ./locales are copied from the mobile app (same keys, same
// languages); keep them in sync when mobile's catalogs change. One web-only
// namespace exists on top: `share` (the group share / Home Assistant modal,
// a web feature the mobile app doesn't have). And web-only `<key>_housing`
// twins of the strings naming a group or category, for Sub-metering projects
// (lib/projectType.ts).
//
// Differences from mobile: localStorage instead of AsyncStorage for the
// explicit choice, and navigator.languages instead of react-native-localize
// for auto-detection. The signed-in user's `me.language` (app_user.language)
// is applied at session load unless the user picked a language on this device.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import no from './locales/no.json';
import sv from './locales/sv.json';
import et from './locales/et.json';

// The languages the app ships UI translations for. The profile language
// picker and locale auto-detection are both constrained to this set.
export const SUPPORTED_LANGUAGES = ['en', 'no', 'sv', 'et'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// The picker always names languages in English, whatever the UI language.
export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  no: 'Norwegian',
  sv: 'Swedish',
  et: 'Estonian',
};

export const FALLBACK_LANGUAGE: SupportedLanguage = 'en';

// localStorage key holding the user's explicit language choice (set in the
// profile page). Same key name as the mobile app's AsyncStorage entry.
export const LANGUAGE_STORAGE_KEY = 'language';

export function isSupportedLanguage(code: string | null | undefined): code is SupportedLanguage {
  return !!code && (SUPPORTED_LANGUAGES as readonly string[]).includes(code.toLowerCase());
}

// Collapse locale tags onto our catalog codes. Norwegian reports as 'nb'
// (Bokmål) or 'nn' (Nynorsk); both map to our 'no'. "sv-SE" → "sv".
export function normalizeLanguage(code: string): string {
  const c = code.toLowerCase().split('-')[0];
  if (c === 'nb' || c === 'nn') return 'no';
  return c;
}

function detectLanguage(): SupportedLanguage {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (isSupportedLanguage(stored)) return stored.toLowerCase() as SupportedLanguage;
    for (const tag of navigator.languages ?? []) {
      const norm = normalizeLanguage(tag);
      if (isSupportedLanguage(norm)) return norm;
    }
  } catch {
    // localStorage can throw in exotic embedding contexts; fall back.
  }
  return FALLBACK_LANGUAGE;
}

// Change the UI language and remember it when the user chose explicitly
// (explicit choices win over the account language on this device).
export function setLanguage(code: string, opts?: { persist?: boolean }): void {
  const norm = normalizeLanguage(code);
  if (!isSupportedLanguage(norm)) return;
  void i18n.changeLanguage(norm);
  if (opts?.persist) {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, norm);
    } catch {
      /* best effort */
    }
  }
}

// Apply the account's language (me.language) unless this device has an
// explicit override. Called once the session/user loads.
export function applyAccountLanguage(code: string | null | undefined): void {
  if (!code) return;
  try {
    if (window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) return;
  } catch {
    /* ignore */
  }
  setLanguage(code);
}

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    no: { translation: no },
    sv: { translation: sv },
    et: { translation: et },
  },
  lng: detectLanguage(),
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
  // Resources are bundled, so init is synchronous — module-level callers
  // (lib/*.ts) can use i18n.t immediately without awaiting. (i18next ≥ 26
  // calls this initAsync; it was initImmediate on mobile's older version.)
  initAsync: false,
  interpolation: {
    escapeValue: false,
  },
});

// Keep <html lang> truthful for assistive tech (WCAG 3.1.1) — index.html
// ships lang="en"; correct it now and on every language switch.
document.documentElement.lang = i18n.language;
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

export default i18n;
